import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Button, Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";
import dayjs, { Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { shallowEqual, useSelector } from "react-redux";
import * as yup from "yup";
import { RootState } from "../../../../redux";
import AlertMessage from "../../../../ui/components/alert-message/alert-message";
import CustomInput from "../../../../ui/components/custom-input/custom-input";
import CustomModal from "../../../../ui/components/custom-modal/custom-modal";
import DateInputFilter from "../../../../ui/components/date-input-filter/date-input-filter";
import DateInput from "../../../../ui/components/date-input/date-input";
import SimpleMessage from "../../../../ui/components/simple-message/simple-message";
import { UserInfo } from "../../../../ui/models/userInfo";
import { ListModel } from "../../../models/list-model";
import { Practica } from "../../../models/practica";
import { PracticaEdit } from "../../../models/practica-edit";
import { practicaServices } from "../../../services/practica.services";
import { getFormattedDate } from "../../../utils/common.utils";
import { handle400Error } from "../../../utils/handle-400-errors";
import "./practica-edit-modal.scss";

export interface PracticaEditModalProps {
  show: boolean;
  practicaId: string;
  handleClose: (success: boolean, item: any) => void;
  refetch: () => void;
}

export function PracticaEditModal(props: PracticaEditModalProps) {
  const { show, practicaId, handleClose, refetch } = props;
  const [practica, setPractica] = useState<Practica>();
  const [formTipos] = useState<ListModel[]>([
    { value: "3", label: "Sin Marcas" },
    { value: "4", label: "Planes Especiales" },
    { value: "44", label: "Internado" },
    { value: "5", label: "Hemodiálisis" },
  ]);
  const [showAlertMessage, setShowAlertMessage] = useState<boolean>(false);
  const [textAlertMessage, setTextAlertMessage] = useState<string>();
  const [fechaMaximaPractica, setFechaMaximaPractica] = useState<Date>();
  const [monthSelected, setMonthSelected] = useState<number>();
  const [yearSelected, setYearSelected] = useState<number>();
  const schema = yup
    .object()
    .shape({
      id: yup.string().trim().required("Campo requerido"),
      fecha: yup.date().required("Campo requerido"),
      cantidad: yup
        .number()
        .typeError("Debe ser un número")
        .min(1, "1 Mínimo")
        .max(99, "99 Máximo")
        .required("Campo requerido"),
    })
    .required();

  const {
    register,
    getFieldState,
    formState,
    handleSubmit,
    formState: { errors },
    setError,
    setValue,
    watch,
    control,
  } = useForm<PracticaEdit>({
    mode: "all",
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    if (practicaId)
      practicaServices
        .getById(practicaId)
        .then((response) => {
          setPractica(response.data);
        })
        .catch();
  }, [practicaId]);

  useEffect(() => {
    CalcularFechaMaxima();
  }, []);

  const handleSubmitGuardar = (data: any) => {
    const fecha = new Date(watch("fecha").toString());
    fecha.setHours(0, 0, 0, 0);
    data.fecha = fecha;

    if (authInfo.role === "ADMIN") {
      if (monthSelected && yearSelected) {
        data.mesAno = new Date(yearSelected, monthSelected, 1);
        data.mesAno.setHours(0, 0, 0, 0);
      } else {
        setTextAlertMessage("Ingrese mes y año de facturación.");
        setShowAlertMessage(true);
        return;
      }
    } else {
      data.mesAno = practica ? new Date(practica?.mesAno) : new Date();
    }

    practicaServices
      .edit(data)
      .then((response: any) => {
        refetch();
        handleClose(true, response.data);
      })
      .catch((error: any) => {
        if (error?.response?.status === 400) {
          handle400Error(error.response.data.errors, setError);
        } else {
          setTextAlertMessage(
            "No se pudo editar la práctica. Intente nuevamente."
          );
          setShowAlertMessage(true);
        }
      });

    props.handleClose(true, data);
  };

  const ActionsComponent = () => {
    return (
      <div className="button-precarga-action">
        <Button
          className="close-button"
          onClick={() => handleClose(false, null)}
        >
          Cancelar
        </Button>
        <Button
          className="create-button"
          variant="contained"
          onClick={handleSubmit((data) => handleSubmitGuardar(data))}
          disabled={formState.disabled}
        >
          Guardar
        </Button>
      </div>
    );
  };

  useEffect(() => {
    if (practica) {
      setValue("id", practica.id, {
        shouldTouch: true,
        shouldDirty: true,
        shouldValidate: true,
      });
      setValue("fecha", practica.fecha, {
        shouldTouch: true,
        shouldDirty: true,
        shouldValidate: true,
      });
      setValue("cantidad", practica.cantidad, {
        shouldTouch: true,
        shouldDirty: true,
        shouldValidate: true,
      });
      setMonthSelected(new Date(practica.mesAno).getMonth());
      setYearSelected(new Date(practica.mesAno).getFullYear());
    }
  }, [practica]);

  const authInfo = useSelector<RootState, UserInfo>(
    ({ auth }: any) => auth.info,
    shallowEqual
  );

  const searchFormTipo = (formTipo: string) => {
    const tipo = formTipos.filter((f) => f.value === formTipo);
    if (tipo.length !== 0) return tipo[0].label;

    return "-";
  };

  const CalcularFechaMaxima = () => {
    const fechaActual = new Date();
    let fechaValidacion = new Date();
    const day = dayjs(fechaActual).date();
    let month = dayjs(fechaActual).month() + 1;
    if (day <= 7) month = month - 1;
    let date = new Date(dayjs(fechaValidacion).year(), month, 0);
    setFechaMaximaPractica(date);
  };

  return (
    <CustomModal
      show={show}
      handleClose={() => handleClose(false, null)}
      title="Modificar Práctica"
      actions={<ActionsComponent />}
      size="lg"
    >
      <div className="practica-edit-modal">
        <Box
          sx={
            practica?.practicaSempre
              ? { height: 620, flexGrow: 1 }
              : { height: 320, flexGrow: 1 }
          }
          component="form"
          autoComplete="off"
        >
          <SimpleMessage
            text="Recuerde modificar con la última fecha de consumo."
            type="warning"
            width="full-width"
          />
          <div className="practica-edit-container">
            <AlertMessage
              text={textAlertMessage ? textAlertMessage : ""}
              type={"error"}
              handleShowExternaly={true}
              show={showAlertMessage}
              setShow={setShowAlertMessage}
            />
            {authInfo.role === "ADMIN" && (
              <Grid
                container
                spacing={2}
                rowSpacing={4}
                sx={{ marginBottom: "20px" }}
              >
                <Grid size={3}>
                  <Typography
                    variant="subtitle2"
                    component="h2"
                    sx={{ fontWeight: 900 }}
                  >
                    Período Facturación
                  </Typography>
                  <DateInputFilter
                    name="mesAno"
                    hiddenDay={true}
                    onChange={(value: Dayjs) => {
                      if (value !== null) {
                        setMonthSelected(value.get("month"));
                        setYearSelected(value.get("year"));
                      } else {
                        setMonthSelected(undefined);
                        setYearSelected(undefined);
                      }
                    }}
                    maxDate={dayjs()}
                    value={
                      monthSelected && yearSelected
                        ? dayjs(new Date(yearSelected, monthSelected, 1))
                        : undefined
                    }
                  />
                </Grid>
                <Grid size={6}>
                  <Typography
                    variant="subtitle2"
                    component="h2"
                    sx={{ fontWeight: 900 }}
                  >
                    Profesional
                  </Typography>
                  <Typography variant="subtitle1" component="text">
                    {practica?.profesionalDescripcion}
                  </Typography>
                </Grid>
              </Grid>
            )}
            <Grid container spacing={2} rowSpacing={4}>
              <Grid size={3}>
                <Controller
                  name="fecha"
                  control={control}
                  render={({ field }) => (
                    <DateInput
                      field={field}
                      name="fecha"
                      label="Fecha"
                      register={register}
                      getFieldState={getFieldState}
                      formState={formState}
                      errors={errors}
                      required={true}
                      value={field.value}
                      maxDate={
                        fechaMaximaPractica ? fechaMaximaPractica : undefined
                      }
                    ></DateInput>
                  )}
                />
              </Grid>
              <Grid size={4}>
                <Typography
                  variant="subtitle2"
                  component="h2"
                  sx={{ fontWeight: 900 }}
                >
                  Obra Social
                </Typography>
                <Typography variant="subtitle1" component="text">
                  {practica?.obraSocialNombre}
                </Typography>
              </Grid>
              <Grid size={5}>
                <Typography
                  variant="subtitle2"
                  component="h2"
                  sx={{ fontWeight: 900 }}
                >
                  Paciente
                </Typography>
                <Typography variant="subtitle1" component="text">
                  {practica?.paciente}
                </Typography>
              </Grid>

              <Grid size={6}>
                <Typography
                  variant="subtitle2"
                  component="h2"
                  sx={{ fontWeight: 900 }}
                >
                  Práctica
                </Typography>
                <Typography variant="subtitle1" component="text">
                  {practica?.practicaCodigo +
                    " - " +
                    practica?.practicaDescripcion}
                </Typography>
              </Grid>
              <Grid size={3}>
                <Typography
                  variant="subtitle2"
                  component="h2"
                  sx={{ fontWeight: 900 }}
                >
                  Precio
                </Typography>
                <Typography variant="subtitle1" component="text">
                  {practica?.precio &&
                    Number.parseFloat((practica?.precio).toString()).toFixed(2)}
                </Typography>
              </Grid>
              <Grid size={3}>
                <div className="input-color">
                  <CustomInput
                    name="cantidad"
                    label="Cantidad"
                    type="number"
                    register={register}
                    getFieldState={getFieldState}
                    formState={formState}
                    errors={errors}
                    required={true}
                    max={90}
                    min={1}
                  ></CustomInput>
                </div>
              </Grid>
            </Grid>
            {practica?.practicaSempre && (
              <Grid
                container
                spacing={2}
                rowSpacing={4}
                sx={{ marginTop: "20px" }}
              >
                <Grid size={3}>
                  <Typography
                    variant="subtitle2"
                    component="h2"
                    sx={{ fontWeight: 900 }}
                  >
                    Form. Bono Serie
                  </Typography>
                  <Typography variant="subtitle1" component="text">
                    {practica?.practicaSempre.formBonoSerie
                      ? practica?.practicaSempre.formBonoSerie
                      : "-"}
                  </Typography>
                </Grid>
                <Grid size={3}>
                  <Typography
                    variant="subtitle2"
                    component="h2"
                    sx={{ fontWeight: 900 }}
                  >
                    Form. Bono Número
                  </Typography>
                  <Typography variant="subtitle1" component="text">
                    {practica?.practicaSempre.formBonoNum}
                  </Typography>
                </Grid>
                <Grid size={3}>
                  <Typography
                    variant="subtitle2"
                    component="h2"
                    sx={{ fontWeight: 900 }}
                  >
                    Form. Bono Sub Número
                  </Typography>
                  <Typography variant="subtitle1" component="text">
                    {practica?.practicaSempre.formBonoSubNum
                      ? practica?.practicaSempre.formBonoSubNum
                      : "-"}
                  </Typography>
                </Grid>
                <Grid size={3}>
                  <Typography
                    variant="subtitle2"
                    component="h2"
                    sx={{ fontWeight: 900 }}
                  >
                    Form. Tipo
                  </Typography>
                  <Typography variant="subtitle1" component="text">
                    {searchFormTipo(practica?.practicaSempre.formTipo)}
                  </Typography>
                </Grid>

                <Grid size={3}>
                  <Typography
                    variant="subtitle2"
                    component="h2"
                    sx={{ fontWeight: 900 }}
                  >
                    Fecha Prescripción
                  </Typography>
                  <Typography variant="subtitle1" component="text">
                    {getFormattedDate(
                      practica?.practicaSempre.fechaPrescripcion.toString()
                    )}
                  </Typography>
                </Grid>
                <Grid size={3}>
                  <Typography
                    variant="subtitle2"
                    component="h2"
                    sx={{ fontWeight: 900 }}
                  >
                    Fecha Autorización
                  </Typography>
                  <Typography variant="subtitle1" component="text">
                    {getFormattedDate(
                      practica?.practicaSempre.fechaAutorizacion.toString()
                    )}
                  </Typography>
                </Grid>
                <Grid size={6}>
                  <Typography
                    variant="subtitle2"
                    component="h2"
                    sx={{ fontWeight: 900 }}
                  >
                    Número de Validación
                  </Typography>
                  <Typography variant="subtitle1" component="text">
                    {practica?.practicaSempre.numeroValidacion}
                  </Typography>
                </Grid>

                <Grid size={3}>
                  <Typography
                    variant="subtitle2"
                    component="h2"
                    sx={{ fontWeight: 900 }}
                  >
                    Código Diagnóstico I
                  </Typography>
                  <Typography variant="subtitle1" component="text">
                    {practica?.practicaSempre.codigoDiagnosticoI
                      ? practica?.practicaSempre.codigoDiagnosticoI
                      : "-"}
                  </Typography>
                </Grid>
                <Grid size={3}>
                  <Typography
                    variant="subtitle2"
                    component="h2"
                    sx={{ fontWeight: 900 }}
                  >
                    Código Diagnóstico II
                  </Typography>
                  <Typography variant="subtitle1" component="text">
                    {practica?.practicaSempre.codigoDiagnosticoII
                      ? practica?.practicaSempre.codigoDiagnosticoII
                      : "-"}
                  </Typography>
                </Grid>
                <Grid size={3}>
                  <Typography
                    variant="subtitle2"
                    component="h2"
                    sx={{ fontWeight: 900 }}
                  >
                    Código Diagnóstico III
                  </Typography>
                  <Typography variant="subtitle1" component="text">
                    {practica?.practicaSempre.codigoDiagnosticoIII
                      ? practica?.practicaSempre.codigoDiagnosticoIII
                      : "-"}
                  </Typography>
                </Grid>
                <Grid size={3}>
                  <Typography
                    variant="subtitle2"
                    component="h2"
                    sx={{ fontWeight: 900 }}
                  >
                    Número Internación
                  </Typography>
                  <Typography variant="subtitle1" component="text">
                    {practica?.practicaSempre.internacionNumero
                      ? practica?.practicaSempre.internacionNumero
                      : "-"}
                  </Typography>
                </Grid>
              </Grid>
            )}
          </div>
        </Box>
      </div>
    </CustomModal>
  );
}

export default PracticaEditModal;
